import { images } from "assets/images";
import { useTranslation } from "react-i18next";
import { convertISOtoDMY } from "common/helpers/convertISO";
import Announcement from "components/Announcement";
import ModalBootstrap from "components/bootstrap/ModalBootstrap";
import HorizontalLine from "components/htmlElements/Line";
import SubTitle from "components/SubTitle";
import Text from "components/Text";
import { ReporterInformationModel } from "models/ReportModel";
import React, { useState } from "react";
import { Image } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import {
  useGetHistoryForLicenseQuery,
  useGetReporterInformationQuery,
} from "features/api/reports/reportsSlice";
import { HistoryForLicenseResponse } from "models/HistoryForLicense";
import { useReports } from "hooks/useReports";
import { renderTagName } from "common/helpers/useTagsName";
import { useGetTextForCodeValue } from "hooks/useDataCodeValue";
import { useRole } from "hooks/useRole";
import { UserRole } from "components/Reports/reportAssets/assets";
import HistoryForLicense from "./HistoryForLicense";
import Labels from "./Labels";
import OffenseLocationMap from "./formSteps/OffenseLocationStepComponents/OffenseLocationMap";

type AccompanyingInformationsProp = {
  licenseNumber: string;
  reportId: number;
};
const AccompanyingInformations = ({
  licenseNumber,
  reportId,
}: AccompanyingInformationsProp) => {
  const { t } = useTranslation();
  const [historyModalShow, setHistoryModalShow] = useState(false);
  const [mapModalShow, setMapModalShow] = useState(false);
  const [showReporterPopup, setShowReporterPopup] = useState(false);

  const role = useRole();

  const { data: historyForLicense }: { data: HistoryForLicenseResponse } =
    useGetHistoryForLicenseQuery({
      licenseNumber,
      reportId,
    });
  const { data }: { data: ReporterInformationModel } =
    useGetReporterInformationQuery<ReporterInformationModel>({
      reportID: reportId,
      role,
    });
  const reporterInformation = data as ReporterInformationModel;
  const { tags } = useReports();

  const offenceLabel = useGetTextForCodeValue(
    reporterInformation?.reportOffenceId,
    "offences"
  );

  let dataFromApp;
  let tagsToRender;
  if (reporterInformation) {
    dataFromApp = [
      {
        title: t("labelReportName"),
        text: reporterInformation.reporterFullName || t("anonymous"),
        disable: role === UserRole.SENIOR_WATCH,
      },
      {
        title: t("characteristic"),
        text: reporterInformation.reporterCharacteristic,
        disable: false,
      },
      {
        title: t("violationTime"),
        text: convertISOtoDMY(reporterInformation.reportCreationDate),
        disable: false,
      },
      {
        title: t("roadType"),
        text: t("interurban"),
        disable: !reporterInformation.reportIsInterstate,
      },
      {
        title: t("city"),
        text: reporterInformation.reportLocationCityName,
        disable: reporterInformation.reportIsInterstate,
      },
      {
        title: t("labelRoad"),
        text: reporterInformation.reportLocationRoad,
        disable: !reporterInformation.reportIsInterstate,
      },
      {
        title: t("street"),
        text: reporterInformation.reportLocationStreetName,
        disable: reporterInformation.reportIsInterstate,
      },
      {
        title: t("labelKm"),
        text: reporterInformation.reportLocationKm,
        disable: !reporterInformation.reportIsInterstate,
      },
      {
        title: t("houseNumber"),
        text: reporterInformation.reportLocationHouseNum,
        disable: reporterInformation.reportIsInterstate,
      },
      {
        title: t("manufacturerName"),
        text: reporterInformation.reportCarMn,
        disable: false,
      },
      {
        title: t("carModel"),
        text: reporterInformation.reportModelType,
        disable: false,
      },
      {
        title: t("carColor"),
        text: reporterInformation.reportCarColor,
        disable: false,
      },
      {
        title: t("licenseNumber"),
        text: reporterInformation.reportLicensePlateNumber,
        disable: false,
      },
      {
        title: t("modelType"),
        text: reporterInformation.reportCarModel,
        disable: false,
      },
      { title: "", text: "", disable: false },
      {
        title: t("offenceType"),
        text: offenceLabel,
        disable: false,
      },
      {
        title: t("reportDescription"),
        text: reporterInformation.reportDescription,
        disable: false,
      },
    ];
    tagsToRender = reporterInformation.reportTags?.map((tag, index) => ({
      key: `${renderTagName(tag, tags)}+${index + 1}`,
      tagName: renderTagName(tag, tags),
      active: true,
    }));
  }

  const togglePopup = () => {
    setShowReporterPopup((prev) => !prev);
  };

  return (
    <div className="detailsContainer spaceTop">
      <ModalBootstrap
        show={showReporterPopup}
        onHide={() => setShowReporterPopup(false)}
        title={t("reporterInformationPopupTitle")}
      >
        <div className="reprter-information">
          <p>{`${t("fullName")} : ${reporterInformation?.reporterFullName}`}</p>
          <p>{`${t("email")} : ${
            reporterInformation?.reporterEmailAddress
          }`}</p>
          <p>{`${t("phone")} : ${reporterInformation?.reporterPhoneNumber}`}</p>
          <p>{`${t("totalReports")} : ${
            reporterInformation?.reporterReportsCount
          }`}</p>
          <p>{`${t("isEntitledCamera")} ${
            reporterInformation?.isEntitledCamera ? t("yes") : t("no")
          }`}</p>
          <p>{`${t("isVerifyByMimshalZamin")} ${
            reporterInformation?.isVerifyByMimshalZamin ? t("yes") : t("no")
          }`}</p>
        </div>
      </ModalBootstrap>
      <h2 className="title borderBottom">{t("accompanyingInformation")}</h2>

      {reporterInformation?.reportDbNotMatch && (
        <Announcement>{t("attentionAnnouncmentLicsenceNumber")}</Announcement>
      )}
      <div className="accompanyingDetailItems">
        {dataFromApp?.map(
          (dt, index) =>
            !dt.disable && (
              <div
                className="dtBox"
                key={`${dt.text}_${index + 1}`}
                style={{
                  width: `${
                    dt.title === t("reportDescription") ||
                    dt.title === t("offenceType")
                      ? "100%"
                      : "33%"
                  }`,
                }}
              >
                <div className="titleDt">{dt.title}</div>
                <div className="textDt">
                  {dt.title === t("labelReportName") && (
                    <InfoCircle
                      className="reporterName"
                      onClick={togglePopup}
                    />
                  )}
                  {dt.text}
                  {(dt.title === t("city") || dt.title === t("labelRoad")) && (
                    <Image
                      src={images.googleMap}
                      alt={t("googleMap")}
                      onClick={() => setMapModalShow(true)}
                    />
                  )}
                </div>
              </div>
            )
        )}
        {reporterInformation != null &&
          reporterInformation.reportRejectionReasonUpdate !== null && (
            <>
              <HorizontalLine color="light-grey" />
              <section className="tags-section">
                <SubTitle>{t("volunteerRejectionReasonOther")}</SubTitle>
                {reporterInformation.reportRejectionReasonUpdate}
              </section>
            </>
          )}
        {tagsToRender?.length > 0 && (
          <>
            <HorizontalLine color="light-grey" />
            <section className="tags-section">
              <SubTitle>{t("labeling")}</SubTitle>
              <Labels tags={tagsToRender} isButton={false} />
            </section>
          </>
        )}
        <HorizontalLine color="light-grey" />
        <section className="summary-setion">
          {historyForLicense && (
            <div className="felx-container">
              <Text>{t("reportsHistoric")}</Text>
              &nbsp;
              <Text
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "18px",
                  opacity: 0.8,
                }}
              >
                {`(${historyForLicense?.historyForLicenseNumber.length})`}
              </Text>
            </div>
          )}
          {historyForLicense?.historyForLicenseNumber.length ? (
            <button
              disabled={!historyForLicense?.historyForLicenseNumber.length}
              type="button"
              className="btn arrowLeft unset textDt"
              onClick={() => {
                setHistoryModalShow(true);
              }}
            >
              {t("toAllReports")}
            </button>
          ) : null}
          <ModalBootstrap
            onHide={() => setHistoryModalShow(false)}
            show={historyModalShow}
          >
            <HistoryForLicense
              data={historyForLicense}
              licenseNumber={licenseNumber}
            />
          </ModalBootstrap>

          <ModalBootstrap
            onHide={() => setMapModalShow(false)}
            show={mapModalShow}
            title={t("wizardStep2Title")}
          >
            <OffenseLocationMap
              incidentCoords={{
                lat: Number(data?.reportLocationLatitude),
                lng: Number(data?.reportLocationLongitude),
              }}
            />
          </ModalBootstrap>
        </section>
        {historyForLicense?.hasDuplicateReportAlert && (
          <p className="requiredField">{t("duplicateReportAlert")}</p>
        )}
      </div>
    </div>
  );
};
export default AccompanyingInformations;
